import type {PromiseOfResponseWithData} from "../helpers/ajax"
import {postDownload, postJson, postJsonStd, resolveRoute} from "../helpers/ajax"
import * as kb from '../kb'
import BookingDriverTable from '../components/BookingDriverTable'
import type {ShowBookingSegment} from '../google-maps'
import {formatAddress, GOOGLE_MAP_COLORS} from '../google-maps'
import {appendComponent, appendPopup, renderFragment} from "../helpers/react-helpers"
import type {Marker} from '../components/StaticGoogleMap'
import StaticGoogleMap from '../components/StaticGoogleMap'
import {lazy} from "react"
import {maybeRegisterChangeHandler, maybeRegisterClickHandler} from '../kb'

interface ShowBookingOptions {
    bookingId: number;
    confirmBookingUrl: string;
    bookingIsConfirmed: boolean;
    segments: ShowBookingSegment[];
    googleMapsApiKey: string;
    distanceUnit: string;
    userIsCustomer: boolean;
}

const PrintBookingDialog = lazy(() => import('../components/PrintBookingDialog'))
const GroupBookingDialog = lazy(() => import('../components/GroupBookingDialog'))
const CommunicationHistoryDialog = lazy(() => import('../components/CommunicationHistoryDialog'))
const AlertHistoryDialog = lazy(() => import('../components/AlertHistoryDialog'))
const GnetFarmOutDialog = lazy(() => import('../components/GnetFarmOutDialog'))
const EmailBookingDialog = lazy(() => import('../components/EmailBookingDialog'))

export default function ShowBookingPage({
    bookingId,
    segments,
    userIsCustomer,
}: ShowBookingOptions) {
    // --- Clone Button ---

    maybeRegisterClickHandler<HTMLButtonElement>('#clone-trip-btn', (cloneBtn) => {
        cloneBtn.disabled = true

        postJson(resolveRoute('bookings.cloneTrip', {id: bookingId}))
            .finally(() => {
                cloneBtn.disabled = false
            })
            .then((resp: any) => {
                if(resp.ok) {
                    kb.notify({
                        text: `Created booking ${resp.data.booking.number}`,
                        className: 'success',
                    })
                    if(resp.data.booking) {
                        kb.navigate(resolveRoute('bookings.edit', {id: resp.data.booking.id}))
                    }
                } else {
                    kb.notify({
                        text: "Could not clone booking",
                        className: 'error',
                    })
                }
            }, (err: any) => {
                kb.notify({
                    text: "Network error. Please try again!",
                    className: 'error',
                })
            })
    })


    // --- Booking Status Dropdown ---
    maybeRegisterChangeHandler<HTMLSelectElement>('#booking-status-dropdown', (statusDropdown) => {
        let actionPromise: PromiseOfResponseWithData<never> | null = null

        switch(statusDropdown.value) {
            case 'confirmed':
                actionPromise = postJson(resolveRoute('bookings.confirmTrip', {id: bookingId}))
                break
            case 'quote':
                actionPromise = postJson(resolveRoute('bookings.confirmTrip', {id: bookingId}), {unconfirm: true})
                break
            case 'cancelled':
                actionPromise = postJson(resolveRoute('bookings.cancel', {id: bookingId}))
                break
        }

        if(actionPromise) {
            actionPromise.then((resp) => {
                if(resp.ok) {
                    kb.refresh()
                } else {
                    throw new Error('Unknown error changing status')
                }
            }).catch((err: any) => {
                if(__DEV__) console.error("Error changing booking status:", err)
                kb.notify({
                    text: "Error processing status change",
                    className: 'error',
                })
            })
        }
    })

    // --- Render Drivers Table ---
    const driversContainer = document.getElementById('drivers-container')
    if(driversContainer) {
        renderFragment(<BookingDriverTable bookingId={bookingId} />, driversContainer)
    }

    // --- Render Static Google Map ---
    const mapPlaceholder = document.getElementById('google-map-placeholder')
    if(mapPlaceholder) {
        const markers: Marker[] = segments.flatMap((seg, segIdx) => seg.stops.map((stop, stopIdx) => ({
            style: {
                color: GOOGLE_MAP_COLORS[segIdx % GOOGLE_MAP_COLORS.length].replace(/^#/, '0x'),
            },
            location: formatAddress(stop),
        })))
        renderFragment(<StaticGoogleMap width={575} height={500} markers={markers} />, mapPlaceholder)
    } else if(__DEV__) {
        console.error("No map placeholder found")
    }

    // --- Print Button ---
    maybeRegisterClickHandler<HTMLButtonElement>('#print-btn', btn => {
        if(userIsCustomer) {
            window.print()
        } else {
            appendComponent(PrintBookingDialog)
        }
    })

    // --- Communication History Button ---
    maybeRegisterClickHandler<HTMLButtonElement>('#communication-history', btn => {
        appendPopup(unmount => <CommunicationHistoryDialog unmount={unmount} bookingId={bookingId} />)
    })

    // --- GNet Farm Out Button ---
    maybeRegisterClickHandler<HTMLButtonElement>('#gnet-farm-out', btn => {
        appendPopup(unmount => <GnetFarmOutDialog onCancel={unmount} bookingId={bookingId} />)
    })

    // --- Alert History Button ---
    maybeRegisterClickHandler<HTMLButtonElement>('#alert-history', btn => {
        appendPopup(unmount => <AlertHistoryDialog unmount={unmount} bookingId={bookingId} />)
    })

    // --- Generate Invoice Button ---
    maybeRegisterClickHandler<HTMLButtonElement>('#generate-invoice-btn', generateInvoiceBtn => {
        generateInvoiceBtn.disabled = true
        postDownload({
            url: resolveRoute('bookings.invoice', {id: bookingId}),
            filename: `Invoice.pdf`
        }).finally(() => {
            generateInvoiceBtn.disabled = false
        })
    })

    // --- Group Booking Button ---
    maybeRegisterClickHandler<HTMLButtonElement>('#group-booking-btn', btn => {
        appendPopup(unmount => <GroupBookingDialog unmount={unmount} bookingId={bookingId} />)
    })

    // --- Mark Quote As Seen Button ---
    maybeRegisterClickHandler<HTMLButtonElement>('#mark-quote-as-seen', markQuoteSeenBtn => {
        markQuoteSeenBtn.disabled = true
        postJsonStd({
            route: ['bookings.markQuoteAsSeen', {id: bookingId}],
        }).catch((err) => {
            markQuoteSeenBtn.disabled = false
        })
    })

    maybeRegisterClickHandler<HTMLButtonElement>('#send-confirmation-btn', btn => {
        appendPopup(unmount => <EmailBookingDialog unmount={unmount} bookingId={bookingId} />)
    })
}
